import { getOwnedResource } from "@/features/adapters/model/runtime/ownership";
import {
  getAdapterPersistenceId,
  getStepResourceDeclarations,
  getResourceDriver,
  matchesAdapterAnswers,
  type AdapterApplyAnswers,
  type AdapterDefinition,
  type AdapterOnboardingChecklistItem,
  type AdapterOnboardingReadinessContent,
  type AdapterWorkflowPrerequisite,
  type ReleaseWithAdapterCollections,
} from "@/features/adapters/model";
import {
  areStepBlocksApplicable,
  getAdapterStepGate,
  getAdapterStepStatus,
  getAdapterStepTraits,
  hasAdapterStepOutstandingItems,
  isAdapterStepComplete,
} from "@/features/adapters/stepRegistry";
import type {
  AdapterBlockStatus,
  AdapterStepEvaluationContext,
  AdapterWizardStep,
} from "@/features/adapters/stepTypes";

export type { AdapterWizardStep } from "@/features/adapters/stepTypes";

export type AdapterWizardStepPrerequisite = AdapterWorkflowPrerequisite;

export interface AdapterWizardStepAccess {
  accessible: boolean;
  reason?: string;
}

export const buildAdapterWizardSteps = (
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
  release?: ReleaseWithAdapterCollections,
): AdapterWizardStep[] => {
  const existingAdapter = release?.adapters?.find((adapter) => adapter.format === 2 && adapter.id === getAdapterPersistenceId(definition.id));
  const steps = definition.workflow.flatMap((step): AdapterWizardStep[] => {
    const traits = getAdapterStepTraits(step);
    const resources = getStepResourceDeclarations(step);
    // A step that no longer applies stays visible only while a driver still has something to remove.
    const applicable =
      matchesAdapterAnswers(answers, step.showWhen) ||
      (Boolean(getOwnedResource(existingAdapter?.ownedResources, step.key)) &&
        resources.some((declaration) => getResourceDriver(declaration).ensureWhenInapplicable));
    if (!applicable || !areStepBlocksApplicable(step, answers)) {
      return [];
    }
    const described = resources
      .map((declaration) => (getResourceDriver(declaration).describe as ((declaration: unknown) => string | undefined) | undefined)?.(declaration))
      .find(Boolean);
    return [{
      ...step,
      label: step.label ?? described ?? traits.defaultLabel,
      requiresConfiguration: step.requiresConfiguration ?? traits.requiresConfiguration,
    }];
  });
  const activeKeys = new Set(steps.map((step) => step.key));
  return steps.map((step) => ({
    ...step,
    prerequisites: step.prerequisites?.filter((prerequisite) => activeKeys.has(prerequisite.stepKey)),
  }));
};

/** Each step's status, the worst of its blocks'. */
export const getAdapterStepStatuses = (
  steps: AdapterWizardStep[],
  evaluation: AdapterStepEvaluationContext,
): Record<string, AdapterBlockStatus> => {
  const statuses: Record<string, AdapterBlockStatus> = {};
  const completion: Record<string, boolean> = {};
  const evaluate = (step: AdapterWizardStep) => {
    statuses[step.key] = getAdapterStepStatus({ ...evaluation, step, steps, completion });
    completion[step.key] = statuses[step.key] === "success";
  };
  for (const step of steps) if (!getAdapterStepTraits(step).aggregate) evaluate(step);
  // Aggregate steps (finish) read the completion of every step evaluated above.
  for (const step of steps) if (getAdapterStepTraits(step).aggregate) evaluate(step);
  return statuses;
};

/** The steps with something left to do that doesn't hold them up, such as the contact entity on Tech1's entities step. */
export const getAdapterStepOutstanding = (
  steps: AdapterWizardStep[],
  evaluation: AdapterStepEvaluationContext,
): Record<string, boolean> =>
  Object.fromEntries(steps.map((step) => [
    step.key,
    hasAdapterStepOutstandingItems({ ...evaluation, step, steps, completion: {} }),
  ]));

export const getAdapterStepCompletion = (
  steps: AdapterWizardStep[],
  evaluation: AdapterStepEvaluationContext,
): Record<string, boolean> =>
  Object.fromEntries(Object.entries(getAdapterStepStatuses(steps, evaluation)).map(([key, status]) => [key, status === "success"]));

export const getAdapterEffectiveStepCompletion = (
  steps: AdapterWizardStep[],
  automatic: Record<string, boolean>,
  manual: Record<string, boolean>,
  evaluation?: AdapterStepEvaluationContext,
): Record<string, boolean> => {
  const completion = Object.fromEntries(steps.map((step) => [
    step.key,
    Boolean(automatic[step.key] || (!getAdapterStepTraits(step).aggregate && manual[step.key])),
  ]));
  if (evaluation) {
    for (const step of steps) {
      if (getAdapterStepTraits(step).aggregate) completion[step.key] = isAdapterStepComplete({ ...evaluation, step, steps, completion });
    }
  }
  return completion;
};

export const clearAutomaticallyCompletedManualSteps = (
  manual: Record<string, boolean>,
  automatic: Record<string, boolean>,
): Record<string, boolean> => Object.fromEntries(
  Object.entries(manual).filter(([key]) => !automatic[key]),
);

interface AdapterStepAccessContext extends AdapterStepEvaluationContext {
  currentStepIndex: number;
  /** Steps the user has been on (see getAdapterVisitedSteps). */
  visitedSteps: string[];
  completion: Record<string, boolean>;
}

export const getAdapterStepAccess = (
  steps: AdapterWizardStep[],
  context: AdapterStepAccessContext,
): AdapterWizardStepAccess[] => {
  const visitedSteps = new Set([
    ...context.visitedSteps,
    ...Object.entries(context.completion)
      .filter(([, complete]) => complete)
      .map(([stepKey]) => stepKey),
    steps[context.currentStepIndex]?.key,
  ]);
  const gates = steps.map((step) => getAdapterStepGate({ ...context, step, steps }));
  const gateIndex = gates.findIndex(Boolean);

  return steps.map((step, index) => {
    if (gateIndex >= 0 && index > gateIndex) return { accessible: false, reason: gates[gateIndex] };
    if (index <= context.currentStepIndex) return { accessible: true };

    for (const prerequisite of step.prerequisites ?? []) {
      const prerequisiteStep = steps.find((candidate) => candidate.key === prerequisite.stepKey);
      const satisfied =
        prerequisite.requirement === "complete"
          ? Boolean(context.completion[prerequisite.stepKey])
          : visitedSteps.has(prerequisite.stepKey);
      if (!satisfied) {
        return {
          accessible: false,
          reason:
            prerequisite.reason ??
            `${prerequisite.requirement === "complete" ? "Complete" : "Visit"} ${prerequisiteStep?.label ?? "the previous step"} first.`,
        };
      }
    }

    return { accessible: true };
  });
};

export const getNextAccessibleAdapterStepIndex = (
  access: AdapterWizardStepAccess[],
  currentStepIndex: number,
): number | undefined => {
  const nextOffset = access.slice(currentStepIndex + 1).findIndex((step) => step.accessible);
  return nextOffset >= 0 ? currentStepIndex + nextOffset + 1 : undefined;
};

export const getApplicableChecklistItems = (
  items: AdapterOnboardingChecklistItem[] | undefined,
  answers: AdapterApplyAnswers,
): AdapterOnboardingChecklistItem[] =>
  (items ?? []).filter((item) => matchesAdapterAnswers(answers, item.showWhen));

export interface AdapterReadinessItem {
  key: "configured" | "validated" | "productionReady";
  title: string;
  description: string;
  ready: boolean;
}

export const getAdapterReadiness = (
  content: AdapterOnboardingReadinessContent | undefined,
  configured: boolean,
  validated: boolean,
): AdapterReadinessItem[] => {
  const resolvedContent = content ?? {
    configured: { title: "Configured", description: "The required connections, mappings, and file routes are ready." },
    validated: { title: "Validated", description: "The integration has been checked with representative data." },
    productionReady: {
      title: "Production-ready",
      description: "The setup is complete and the required checks have passed.",
    },
  };

  return [
    { key: "configured", ...resolvedContent.configured, ready: configured },
    { key: "validated", ...resolvedContent.validated, ready: validated },
    { key: "productionReady", ...resolvedContent.productionReady, ready: configured && validated },
  ];
};
